/* Amares Homes — a magnifying lens over the product image.
 *
 * Craft ships a hover zoom, but it swaps the whole frame for a blown-up
 * background: you lose sight of the bed while inspecting the weave. This is a
 * loupe instead — a small square held beside the pointer showing the fabric at
 * the cursor, with the photograph still whole underneath.
 *
 * The lens never takes the pointer, so the click straight through it still
 * opens the lightbox. Pointer devices only: there is no hover on a phone, and
 * a lens pinned under a fingertip would cover the thing it magnifies.
 */
(function () {
  'use strict';

  var FINE_POINTER = window.matchMedia('(hover: hover) and (pointer: fine)');
  var OFFSET = 26;

  var lens = null;
  var loaded = {};

  function makeLens() {
    if (lens) return lens;
    lens = document.createElement('div');
    lens.className = 'amx-loupe';
    lens.setAttribute('aria-hidden', 'true');
    document.body.appendChild(lens);
    return lens;
  }

  /** The biggest candidate the browser was offered, so the lens has real
   *  detail to show rather than an upscaled thumbnail. */
  function bestSource(img) {
    var set = img.getAttribute('srcset');
    if (!set) return img.currentSrc || img.src;

    var best = null;
    var bestW = 0;
    set.split(',').forEach(function (part) {
      var bits = part.trim().split(/\s+/);
      var w = parseInt(bits[1], 10);
      if (bits[0] && w > bestW) {
        bestW = w;
        best = bits[0];
      }
    });
    return best || img.currentSrc || img.src;
  }

  /* Warm the full-size file once, so the first move does not flash empty. */
  function warm(src) {
    if (loaded[src]) return;
    loaded[src] = true;
    var pre = new Image();
    pre.src = src;
  }

  function hide() {
    if (lens) lens.classList.remove('is-on');
  }

  function bind(gallery) {
    var zoom = parseFloat(gallery.dataset.amxMagnify) || 2.4;

    /* Pointer moves arrive faster than frames, and each one reads layout then
     * writes style. Coalescing into a frame keeps that to once per paint. */
    var pending = null;
    var queued = false;

    var draw = function () {
      queued = false;
      var e = pending;
      if (!e) return;

      if (!FINE_POINTER.matches) return hide();
      // While the lightbox is open the image is already full size.
      if (document.querySelector('product-modal[open], .product-media-modal[open]')) return hide();

      var opener = e.target.closest ? e.target.closest('.product__modal-opener') : null;
      var img = opener && opener.querySelector('img');
      if (!img || !img.complete || !img.naturalWidth) return hide();

      var box = img.getBoundingClientRect();
      var x = e.clientX - box.left;
      var y = e.clientY - box.top;
      // The zoom button covers the frame, so the pointer is tested against the
      // picture itself rather than trusting whatever it happens to be over.
      if (x < 0 || y < 0 || x > box.width || y > box.height) return hide();

      var src = bestSource(img);
      warm(src);

      var el = makeLens();
      var size = el.offsetWidth || 180;

      if (el.dataset.src !== src) {
        el.dataset.src = src;
        el.style.backgroundImage = 'url("' + src + '")';
      }
      var bgW = box.width * zoom;
      var bgH = box.height * zoom;
      el.style.backgroundSize = bgW + 'px ' + bgH + 'px';

      /* Clamped to the picture's own edges. Unclamped, a pointer near a corner
       * fills half the lens with the ground behind it, which looks like the
       * loupe is broken rather than like the edge of the photograph. */
      var bx = Math.min(0, Math.max(size - bgW, -(x * zoom - size / 2)));
      var by = Math.min(0, Math.max(size - bgH, -(y * zoom - size / 2)));
      el.style.backgroundPosition = bx + 'px ' + by + 'px';

      // Beside the pointer, flipping before it would leave the viewport.
      var left = e.clientX + OFFSET;
      var top = e.clientY + OFFSET;
      if (left + size > window.innerWidth - 8) left = e.clientX - OFFSET - size;
      if (top + size > window.innerHeight - 8) top = e.clientY - OFFSET - size;
      el.style.transform = 'translate3d(' + left + 'px,' + top + 'px,0)';
      el.classList.add('is-on');
    };

    gallery.addEventListener(
      'mousemove',
      function (e) {
        // The event object is reused, so keep only what draw() needs.
        pending = { clientX: e.clientX, clientY: e.clientY, target: e.target };
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(draw);
      },
      { passive: true }
    );

    gallery.addEventListener('mouseleave', function () {
      pending = null;
      hide();
    });
  }

  function boot(scope) {
    var roots = (scope || document).querySelectorAll('[data-amx-magnify]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.amxMagnifyBound) return;
      root.dataset.amxMagnifyBound = '1';
      bind(root);
    });
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', function () { boot(); });

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  window.addEventListener('scroll', hide, { passive: true });
})();
